import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getImage } from '@utils/imageHelper';
import textHelper from '@utils/textHelper';
import dateHelper from '@utils/dates';
import P1 from '@base/P1/P1';
import Avatar from '@base/Avatar/Avatar';
import H6 from '@base/H6/H6';
import { Spacings, Fonts } from '@styles/index';
import isSameDay from 'date-fns/isSameDay';
import { getMe } from '@selectors/auth';
import { segment } from '@actions/analytics';
import { GTM_ACTIVITY_CLICKED } from '@constants/segment';
import { sendEvents } from '@events/sendAnalytics';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useIsMobile } from '@stores/AppCommonData';
import { Live, Play } from './CardIcons';
import CardIcon from './CardIcon';
import { buttonTypes, getIsLive } from './scheduleHelper';

const Wrapper = styled.div`
  overflow: hidden;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'not-allowed')};
  display: flex;
  flex-direction: column;
`;

const TagsWrapper = styled.div`
  display: grid;
  align-items: center;
  grid-template-rows: 1fr / max-content 1fr max-content;
  grid-template-columns: ${({ $isLive }) =>
    `${$isLive ? 'max-content' : ''} 1fr max-content;`};
  grid-column-gap: ${Spacings.SPACING_2B};
`;

const Tag = styled(P1)`
  display: inline-block;
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: ${({ $bgColor }) => $bgColor};
`;

const LiveTag = styled(H6)`
  color: var(--color-i-white);
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_2B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-i-red);
`;

const Title = styled.div`
  margin-top: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H4};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-i-black-3);
  min-height: ${Spacings.SPACING_14B};
`;

const AuthorInfo = styled.div`
  padding-top: ${Spacings.SPACING_6B};
  display: grid;
  grid-template: 1fr / max-content 1fr ${Spacings.SPACING_12B};
  grid-column-gap: ${Spacings.SPACING_3B};
  align-items: center;
  margin-top: auto;
`;

const AvatarContainer = styled.div`
  max-width: ${Spacings.SPACING_22B};
  position: relative;
  width: ${({ $count }) => `${40 + 24 * ($count - 1)}px`};
  height: ${Spacings.SPACING_10B};
`;

const StyledAvatar = styled(Avatar)`
  position: absolute;
  border: ${Spacings.SPACING_2} solid var(--color-i-white);
  z-index: ${({ $index }) => 3 - $index};
  left: ${({ $index }) => `${24 * $index}px`};
  top: ${Spacings.SPACING_0B};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'not-allowed')};
`;

const AuthorName = styled(P1)`
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-i-violet-7);
`;

const Duration = styled(AuthorName)`
  text-align: right;
`;

const IconContainer = styled.div`
  position: relative;
  height: 100%;
`;

const StarIcon = styled.img`
  user-drag: none;
`;

const getFullName = (author) => {
  return `${
    author.first_name.charAt(0).toUpperCase() + author.first_name.slice(1)
  } ${author.last_name.charAt(0).toUpperCase() + author.last_name.slice(1)}`;
};

const ScheduleCard = ({ data, className }) => {
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isMobileDevice = useIsMobile();
  const {
    video_duration: lessonDuration,
    programme,
    finished_at,
    is_live: live,
    live_at,
    permalink,
    relative_link: relativeLink,
    youtubeLink,
    isMustWatch,
    isYoutube
  } = data;
  const { authors } = programme;
  const [isLive, setIsLive] = useState(live);
  useEffect(() => {
    setIsLive(getIsLive(data));
  }, [data]);
  const finishedAt =
    finished_at ?? dateHelper.addTime(new Date(live_at), lessonDuration, 's');
  const hasClassEnded = finishedAt
    ? dateHelper.compareDates(finishedAt, new Date()) === 1
    : false;
  const buttonType = useMemo(() => {
    if (isLive) {
      return buttonTypes.live;
    }
    if (hasClassEnded) {
      return buttonTypes.completed;
    }
    return -1;
  }, [hasClassEnded, isLive]);
  const [showButton, setShowButton] = useState(false);
  const onMouseEnter = () => {
    setShowButton(true);
  };
  const summitUID = useSelector(({ summit }) => summit.info.uid);
  const { state, language } = useSelector(getMe);

  const onMouseLeave = () => {
    setShowButton(false);
  };
  const staticIcon = useMemo(() => {
    if (isMobileDevice) {
      if (isLive) return Live;
      if (hasClassEnded) return Play;
    }
    if (isMustWatch && !isLive) {
      return <StarIcon src={getImage('summit/star.svg')} />;
    }
  }, [hasClassEnded, isLive, isMobileDevice, isMustWatch]);

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);

  const topicGroupColorMap = useSelector(
    ({ summit }) => summit.topicGroupColorMap || {}
  );

  // const isClickable = isLive || hasClassEnded;
  const isClickable = !!permalink;

  const authorsAvatars = useMemo(() => {
    return (
      <AvatarContainer $count={Math.min(authors.length, 3)}>
        {authors.slice(0, 3).map(({ author_details: author }, index) => (
          <StyledAvatar
            key={author.username}
            size={Spacings.SPACING_10B}
            src={author.avatar}
            $index={index}
            $isClickable={isClickable}
          />
        ))}
      </AvatarContainer>
    );
  }, [authors, isClickable]);

  const onClick = isClickable
    ? () => {
        if (!isLoggedIn) {
          openLoginDrawer();
        } else {
          const eventData = {
            educator_uid:
              data.programme?.authors?.[0].author_details.uid || 'NA',
            educator_username:
              data.programme?.authors?.[0].author_details.username || 'NA',
            end_date: data.date,
            goal_name: data.programme?.goal?.name || 'NA',
            goal_uid: data.programme?.goal?.uid || 'NA',
            is_enrolled: data.programme?.is_enrolled,
            is_subscription_active: 'NA',
            language,
            last_primary_source: 'NA',
            last_primary_source_section: 'NA',
            start_date: data.date,
            state,
            subscription_duration: 'NA',
            subscription_end_date: 'NA',
            current_screen: 'GTM Page',
            summit_date: data.date,
            is_summit_day: isSameDay(new Date(data.live_at), new Date()),
            summit_uid: summitUID,
            summit_name: data.summitKey,
            class_uid: data.classUid,
            class_name: data.name,
            activity_flag: data.relative_link.includes('/test/')
              ? 'test'
              : 'class'
          };
          sendEvents(GTM_ACTIVITY_CLICKED, eventData, true);
          segment.track(GTM_ACTIVITY_CLICKED, eventData);
          window.open(isYoutube ? youtubeLink : relativeLink, '_blank');
        }
      }
    : undefined;

  const colors =
    topicGroupColorMap[programme.topic_groups[0].uid] ||
    topicGroupColorMap[programme.topic_groups[1].uid] ||
    {};

  return (
    <Wrapper
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      $isClickable={isClickable}
      className={className}
    >
      <TagsWrapper $isLive={isLive}>
        {isLive && <LiveTag>LIVE</LiveTag>}
        <div>
          <Tag color={colors?.textColor} $bgColor={colors?.bgColor}>
            {programme.topic_groups[0].title}
          </Tag>
        </div>
        <Duration>
          {dateHelper.getMinutesValueFromSeconds(lessonDuration)}
        </Duration>
      </TagsWrapper>
      <Title>{programme.name}</Title>
      <AuthorInfo>
        {authorsAvatars}
        <AuthorName>
          {`${getFullName(authors[0])}${
            authors.length > 1
              ? ` and ${textHelper.pluralize(authors.length - 1, 'other')}`
              : ''
          }`}
        </AuthorName>
        <IconContainer>
          {staticIcon}
          {!isMobileDevice && (
            <CardIcon showButton={showButton} buttonType={buttonType} />
          )}
        </IconContainer>
      </AuthorInfo>
    </Wrapper>
  );
};

ScheduleCard.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string
};

ScheduleCard.defaultProps = {
  className: ''
};

export default ScheduleCard;
